import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { getDocumentName } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useCreatePersonMutation } from '../../../forms/ThirdParty/hooks/useCreatePersonMutation.hook';
import { usePersonInitialValuesFactory } from '../../../forms/ThirdParty/hooks/usePersonInitialValuesFactory.hook';
import { personForm2ApiMapper } from '../../../forms/ThirdParty/mappers/person.form2api.mapper';
import { ThirdPartiesCmp_SomePeople_QueryDocument } from '../../../components/ThirdParties/queries/some-people.query';

import { resolveCreationErrorMessage } from '../utils/resolve-creation-error-message.util';
import {
  buildActiveMembershipFromDirector,
  buildPersonInitialValuesFromDirector,
} from '../mappers/director-to-person.mapper';
import { useRetryableCreation } from './useRetryableCreation.hook';
import type { ActiveMembership } from '../types/active-membership.type';
import type { CreationStatus } from '../types/creation-status.type';
import type { OrganizationSearchDirectorPersonnePhysique } from '../types/organization-search-result.type';

export interface DirectorSettledOutcome {
  readonly membership?: ActiveMembership;
  readonly personId?: Id;
  readonly status: 'fulfilled' | 'rejected';
}

interface Params {
  readonly canStart: boolean;
  readonly director: Readonly<OrganizationSearchDirectorPersonnePhysique>;
  readonly fallbackCivilityId: Id;
  readonly onSettled?: (outcome: Readonly<DirectorSettledOutcome>) => void;
}

interface Result {
  readonly errorMessage?: string;
  readonly personId?: Id;
  readonly retry: () => void;
  readonly status: CreationStatus;
}

export const useDirectorPersonCreation = ({
  canStart,
  director,
  fallbackCivilityId,
  onSettled,
}: Readonly<Params>): Result => {
  const queryClient = useQueryClient();
  const { representativeTypesCodeMap } = useReferencesContext();
  const { mutateAsync: createPerson } = useCreatePersonMutation();

  const { initialValues } = usePersonInitialValuesFactory({
    isInternal: false,
    initialValues: buildPersonInitialValuesFromDirector(director, {
      fallbackCivilityId,
    }),
  });

  const [state, setState] = useState<Omit<Result, 'retry'>>({
    status: 'pending',
  });

  const createPersonAndMembership = async () => {
    setState({ status: 'pending' });

    try {
      const { createPerson: createdPerson } = await createPerson({
        person: personForm2ApiMapper(initialValues),
      });

      const membership = buildActiveMembershipFromDirector(director, {
        personId: createdPerson._id,
        representativeTypeId: representativeTypesCodeMap.Main._id,
      });

      setState({ personId: createdPerson._id, status: 'success' });

      await queryClient.invalidateQueries({
        queryKey: [getDocumentName(ThirdPartiesCmp_SomePeople_QueryDocument)],
      });

      onSettled?.({
        membership,
        personId: createdPerson._id,
        status: 'fulfilled',
      });
    } catch (error) {
      setState({
        errorMessage: resolveCreationErrorMessage(error),
        status: 'error',
      });

      onSettled?.({ status: 'rejected' });
    }
  };

  const { retry } = useRetryableCreation({
    canStart,
    perform: createPersonAndMembership,
  });

  return { ...state, retry };
};
